import React, { useEffect, useMemo, useState } from "react";
import { useAuth, formatApiError } from "../../contexts/AuthContext";
import { toast } from "sonner";
import {
  Plus,
  Pencil,
  Trash2,
  Download,
  X,
  Save,
  FileText,
} from "lucide-react";

const DEFAULT_SELLER = {
  company: "EKOBIOTEK UKRAINA, LLC",
  edrpou: "32125693",
  address: "73000, m. Kherson, vul. Raketna, 110a, Ukraine",
  email: "sekretar@ekobiotek-ukraine.com.ua",
  phone: "+380 93 368 1038, +380 94 712 1387",
};

const DEFAULT_BANK = {
  beneficiary: "EKOBIOTEK UKRAINA, LLC",
  bank_name: "Nest Bank S.A Warsaw, Poland",
  iban: "PL88 2530 0008 2060 1091 8389 0002",
  swift: "NESBPLPW",
  currency: "EUR",
  reference: "",
};

const DEFAULT_SHIPPING_DOCS = [
  "Commercial invoice (original + copies)",
  "Packing list",
  "Certificate of origin",
  "Bill of Lading",
  "Phytosanitary certificate",
  "Health certificate",
  "Certificate of quality / analysis",
  "Export customs declaration (copy)",
];

const TRACKING_STEPS = [
  { key: "invoice_sent", label: "Invoice sent" },
  { key: "payment_terms_accepted", label: "Payment terms accepted" },
  { key: "samples_validated", label: "Samples validated" },
  { key: "contract_signed", label: "Contract signed" },
  { key: "payment_received", label: "Payment received" },
];

const DEFAULT_TRACKING = () =>
  TRACKING_STEPS.reduce((acc, s) => ({ ...acc, [s.key]: false }), {});

const DEFAULT_REMARKS =
  "The proforma invoice has been prepared, subject to confirmation of the maximum legally permitted weight on this route.";

const todayISO = () => new Date().toISOString().slice(0, 10);

const EMPTY = () => ({
  invoice_number: "",
  date: todayISO(),
  contract_number: "",
  contract_date: todayISO(),
  currency: "EUR",
  seller: { ...DEFAULT_SELLER },
  buyer: { company: "", idno: "", address: "", representative: "", email: "" },
  incoterms: "DAP (Incoterms® 2020)",
  container_type: "FTL",
  container_count: 1,
  place_of_loading: "Poltava, Ukraine",
  route: "",
  transport: "Road, 1 FTL",
  latest_loading: "",
  origin: "Ukraine",
  freight_component: "",
  estimated_transit_time: "",
  items: [
    {
      description: "",
      hs_code: "1512 19 90",
      packaging_unit: "Bottles",
      qty_mt: 0,
      price_per_mt: 0,
      amount: 0,
    },
  ],
  advance_pct: 10,
  balance_pct: 90,
  bank: { ...DEFAULT_BANK },
  container_loading: "",
  partial_shipment: "Not allowed",
  transshipment: "Not allowed",
  shipping_documents: [...DEFAULT_SHIPPING_DOCS],
  remarks: DEFAULT_REMARKS,
  signature_name: "STAKHMYCH OLEKSANDR",
  signature_title: "Head of Commercial Department",
  stamp_image: "",
  logo_position: "left",
  validity_days: 10,
  tracking: DEFAULT_TRACKING(),
  client_remarks: "",
});

const fmt = (n, d = 2) => {
  const v = Number(n);
  if (!isFinite(v)) return "0.00";
  return v.toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d });
};

const AdminInvoices = () => {
  const { api, token } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/invoices");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []); // eslint-disable-line

  const openNew = () => setEditing(EMPTY());
  const openEdit = (inv) => setEditing({ ...EMPTY(), ...inv });

  const totals = useMemo(() => {
    if (!editing) return { qty: 0, amount: 0 };
    const its = editing.items || [];
    return its.reduce(
      (acc, it) => {
        const q = Number(it.qty_mt) || 0;
        const p = Number(it.price_per_mt) || 0;
        const a = Number(it.amount) || q * p;
        acc.qty += q;
        acc.amount += a;
        return acc;
      },
      { qty: 0, amount: 0 }
    );
  }, [editing]);

  const advanceAmount = (totals.amount * (Number(editing?.advance_pct) || 0)) / 100;
  const balanceAmount = totals.amount - advanceAmount;

  const setItem = (i, patch) => {
    const copy = [...editing.items];
    copy[i] = { ...copy[i], ...patch };
    const q = Number(copy[i].qty_mt) || 0;
    const p = Number(copy[i].price_per_mt) || 0;
    copy[i].amount = Number((q * p).toFixed(2));
    setEditing({ ...editing, items: copy });
  };
  const addItem = () =>
    setEditing({
      ...editing,
      items: [
        ...editing.items,
        { description: "", hs_code: "1512 19 90", packaging_unit: "Bottles", qty_mt: 0, price_per_mt: 0, amount: 0 },
      ],
    });
  const removeItem = (i) =>
    setEditing({ ...editing, items: editing.items.filter((_, idx) => idx !== i) });

  const save = async () => {
    if (!editing) return;
    const body = { ...editing };
    body.advance_pct = Number(body.advance_pct) || 0;
    body.balance_pct = Number(body.balance_pct) || 0;
    try {
      if (editing.id) {
        await api.patch(`/admin/invoices/${editing.id}`, body);
        toast.success("Invoice updated");
      } else {
        await api.post("/admin/invoices", body);
        toast.success("Invoice created");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this proforma invoice?")) return;
    try {
      await api.delete(`/admin/invoices/${id}`);
      setItems((xs) => xs.filter((x) => x.id !== id));
      toast.success("Deleted");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const downloadPdf = async (inv) => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/invoices/${inv.id}/pdf`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Proforma_${(inv.invoice_number || inv.id).replace(/[/ ]/g, "_")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.message || "Download failed");
    }
  };

  return (
    <div className="p-6 md:p-10">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <span className="overline text-[#DE9E36]">Billing</span>
          <h1 className="mt-3 text-3xl md:text-4xl font-bold text-[#1A201C] tracking-tight">
            Proforma invoices
          </h1>
          <p className="mt-2 text-sm text-[#4E5750]">
            {items.length} invoices · generate, edit and export as PDF.
          </p>
        </div>
        <button
          onClick={openNew}
          data-testid="new-invoice-btn"
          className="inline-flex items-center gap-2 px-5 py-3 bg-[#DE9E36] text-[#1A201C] text-xs tracking-[0.22em] uppercase font-bold hover:bg-[#1A201C] hover:text-[#DE9E36] transition-colors"
        >
          <Plus size={16} /> New invoice
        </button>
      </div>

      {loading ? (
        <div className="p-10 text-center text-sm text-[#4E5750]">Loading…</div>
      ) : items.length === 0 ? (
        <div className="p-14 border border-dashed border-[#E5E0D8] text-center text-sm text-[#4E5750]">
          No invoices yet. Click "New invoice" to create your first proforma.
        </div>
      ) : (
        <div className="border border-[#E5E0D8] bg-white overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F3EFE7] text-left">
              <tr>
                <Th>Invoice #</Th>
                <Th>Date</Th>
                <Th>Buyer</Th>
                <Th>Container</Th>
                <Th>Incoterms</Th>
                <Th>Progress</Th>
                <Th className="text-right">Total</Th>
                <Th className="text-right">Actions</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E5E0D8]">
              {items.map((inv) => (
                <tr key={inv.id} className="hover:bg-[#FDFBF7]" data-testid={`invoice-row-${inv.invoice_number}`}>
                  <Td className="font-mono text-xs">{inv.invoice_number}</Td>
                  <Td className="text-[#4E5750]">{inv.date || ""}</Td>
                  <Td className="max-w-xs truncate">{inv.buyer?.company}</Td>
                  <Td className="text-xs font-semibold">
                    {inv.container_count && inv.container_type
                      ? `${inv.container_count} × ${inv.container_type}`
                      : inv.container_type || "—"}
                  </Td>
                  <Td className="text-xs text-[#4E5750]">{inv.incoterms}</Td>
                  <Td>
                    <div
                      className="flex items-center gap-1"
                      data-testid={`invoice-progress-${inv.invoice_number}`}
                      title={TRACKING_STEPS.map((s, i) => `${i + 1}. ${s.label}${inv.tracking?.[s.key] ? " ✓" : ""}`).join("\n")}
                    >
                      {TRACKING_STEPS.map((s) => (
                        <span
                          key={s.key}
                          className={`w-2.5 h-2.5 rounded-full ${
                            inv.tracking?.[s.key] ? "bg-[#DE9E36]" : "bg-[#E5E0D8]"
                          }`}
                        />
                      ))}
                      <span className="ml-1 text-[10px] text-[#4E5750] font-mono">
                        {TRACKING_STEPS.filter((s) => inv.tracking?.[s.key]).length}/5
                      </span>
                    </div>
                  </Td>
                  <Td className="text-right font-semibold">
                    {inv.currency} {fmt(inv.total_amount)}
                  </Td>
                  <Td className="text-right whitespace-nowrap">
                    <button
                      onClick={() => downloadPdf(inv)}
                      data-testid={`invoice-pdf-${inv.invoice_number}`}
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-xs tracking-[0.2em] uppercase border border-[#1A201C] bg-[#1A201C] text-[#DE9E36] hover:bg-[#DE9E36] hover:text-[#1A201C] mr-2"
                    >
                      <Download size={12} /> PDF
                    </button>
                    <button
                      onClick={() => openEdit(inv)}
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-xs tracking-[0.2em] uppercase border border-[#E5E0D8] hover:bg-[#F3EFE7] mr-2"
                    >
                      <Pencil size={12} /> Edit
                    </button>
                    <button
                      onClick={() => remove(inv.id)}
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-xs tracking-[0.2em] uppercase text-red-700 border border-red-200 hover:bg-red-50"
                    >
                      <Trash2 size={12} />
                    </button>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <div
          className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center p-4 overflow-y-auto"
          onClick={() => setEditing(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="bg-[#FDFBF7] w-full max-w-5xl my-6"
            data-testid="invoice-editor"
          >
            <div className="sticky top-0 bg-[#1A201C] text-white px-6 py-4 flex items-center justify-between z-10">
              <div>
                <div className="overline text-[#DE9E36]">
                  {editing.id ? "Edit invoice" : "New invoice"}
                </div>
                <div className="text-lg font-bold flex items-center gap-2">
                  <FileText size={16} />
                  {editing.invoice_number || "PROFORMA"}
                </div>
              </div>
              <button onClick={() => setEditing(null)} className="text-white/60 hover:text-white">
                <X size={20} />
              </button>
            </div>

            <div className="p-6 md:p-8 space-y-7">
              {/* Metadata */}
              <Section title="Invoice metadata">
                <Grid>
                  <Field label="Invoice No.">
                    <Input
                      value={editing.invoice_number}
                      onChange={(v) => setEditing({ ...editing, invoice_number: v })}
                      testId="invoice-number"
                      placeholder="PI-212609/UA_MD"
                    />
                  </Field>
                  <Field label="Date">
                    <Input type="date" value={editing.date || ""} onChange={(v) => setEditing({ ...editing, date: v })} />
                  </Field>
                  <Field label="Contract No.">
                    <Input value={editing.contract_number || ""} onChange={(v) => setEditing({ ...editing, contract_number: v })} />
                  </Field>
                  <Field label="Contract date">
                    <Input type="date" value={editing.contract_date || ""} onChange={(v) => setEditing({ ...editing, contract_date: v })} />
                  </Field>
                  <Field label="Currency">
                    <select
                      value={editing.currency}
                      onChange={(e) => setEditing({ ...editing, currency: e.target.value })}
                      className="w-full h-11 px-3 border border-[#E5E0D8] bg-white text-sm"
                    >
                      <option>EUR</option>
                      <option>USD</option>
                      <option>UAH</option>
                    </select>
                  </Field>
                </Grid>
              </Section>

              {/* Buyer */}
              <Section title="Buyer / Consignee">
                <Grid>
                  <Field label="Company">
                    <Input value={editing.buyer.company} onChange={(v) => setEditing({ ...editing, buyer: { ...editing.buyer, company: v } })} testId="buyer-company" />
                  </Field>
                  <Field label="IDNO / Tax ID">
                    <Input value={editing.buyer.idno || ""} onChange={(v) => setEditing({ ...editing, buyer: { ...editing.buyer, idno: v } })} />
                  </Field>
                  <Field label="Address" wide>
                    <Input value={editing.buyer.address || ""} onChange={(v) => setEditing({ ...editing, buyer: { ...editing.buyer, address: v } })} />
                  </Field>
                  <Field label="Representative">
                    <Input value={editing.buyer.representative || ""} onChange={(v) => setEditing({ ...editing, buyer: { ...editing.buyer, representative: v } })} />
                  </Field>
                  <Field label="Email">
                    <Input value={editing.buyer.email || ""} onChange={(v) => setEditing({ ...editing, buyer: { ...editing.buyer, email: v } })} />
                  </Field>
                </Grid>
              </Section>

              {/* Logistics */}
              <Section title="Logistics & delivery">
                <Grid>
                  <Field label="Incoterms">
                    <Input value={editing.incoterms || ""} onChange={(v) => setEditing({ ...editing, incoterms: v })} />
                  </Field>
                  <Field label="Container / Load type">
                    <select
                      value={editing.container_type || ""}
                      onChange={(e) => setEditing({ ...editing, container_type: e.target.value })}
                      data-testid="container-type"
                      className="w-full h-11 px-3 border border-[#E5E0D8] bg-white text-sm"
                    >
                      <option value="">— Select —</option>
                      <option value="20 FCL">20 FCL</option>
                      <option value="40 FCL">40 FCL</option>
                      <option value="FTL">FTL</option>
                      <option value="FLEXITANK">FLEXITANK</option>
                    </select>
                  </Field>
                  <Field label="Number of containers">
                    <Input
                      type="number"
                      value={editing.container_count ?? ""}
                      onChange={(v) => setEditing({ ...editing, container_count: v === "" ? null : Number(v) })}
                      placeholder="1"
                      testId="container-count"
                    />
                  </Field>
                  <Field label="Place of loading">
                    <Input value={editing.place_of_loading || ""} onChange={(v) => setEditing({ ...editing, place_of_loading: v })} />
                  </Field>
                  <Field label="Route" wide>
                    <Input value={editing.route || ""} onChange={(v) => setEditing({ ...editing, route: v })} />
                  </Field>
                  <Field label="Transport">
                    <Input value={editing.transport || ""} onChange={(v) => setEditing({ ...editing, transport: v })} />
                  </Field>
                  <Field label="Latest loading">
                    <Input type="date" value={editing.latest_loading || ""} onChange={(v) => setEditing({ ...editing, latest_loading: v })} />
                  </Field>
                  <Field label="Origin">
                    <Input value={editing.origin || ""} onChange={(v) => setEditing({ ...editing, origin: v })} />
                  </Field>
                </Grid>
              </Section>

              {/* Items */}
              <Section title="Items">
                <div className="overflow-x-auto border border-[#E5E0D8]">
                  <table className="w-full text-xs">
                    <thead className="bg-[#F3EFE7]">
                      <tr>
                        <ThSm>#</ThSm>
                        <ThSm>Description</ThSm>
                        <ThSm>HS code</ThSm>
                        <ThSm>Packaging</ThSm>
                        <ThSm>Qty (MT)</ThSm>
                        <ThSm>Price ({editing.currency}/MT)</ThSm>
                        <ThSm>Amount</ThSm>
                        <ThSm />
                      </tr>
                    </thead>
                    <tbody>
                      {editing.items.map((it, i) => (
                        <tr key={i} className="border-t border-[#E5E0D8]">
                          <TdSm>{i + 1}</TdSm>
                          <td className="p-1">
                            <input
                              value={it.description}
                              onChange={(e) => setItem(i, { description: e.target.value })}
                              placeholder="Description"
                              className="w-full h-9 px-2 border border-[#E5E0D8] bg-white text-xs"
                            />
                          </td>
                          <td className="p-1">
                            <input
                              value={it.hs_code || ""}
                              onChange={(e) => setItem(i, { hs_code: e.target.value })}
                              className="w-24 h-9 px-2 border border-[#E5E0D8] bg-white text-xs"
                            />
                          </td>
                          <td className="p-1">
                            <select
                              value={it.packaging_unit || "Bottles"}
                              onChange={(e) => setItem(i, { packaging_unit: e.target.value })}
                              data-testid={`item-packaging-${i}`}
                              className="w-28 h-9 px-2 border border-[#E5E0D8] bg-white text-xs"
                            >
                              <option value="Bottles">Bottles</option>
                              <option value="IBC">IBC</option>
                              <option value="Drums">Drums</option>
                              <option value="Big bags">Big bags</option>
                              <option value="Flexitank">Flexitank</option>
                              <option value="Bulk">Bulk</option>
                            </select>
                          </td>
                          <td className="p-1">
                            <input
                              type="number"
                              step="0.001"
                              value={it.qty_mt ?? 0}
                              onChange={(e) => setItem(i, { qty_mt: e.target.value })}
                              data-testid={`item-qty-${i}`}
                              className="w-24 h-9 px-2 border border-[#E5E0D8] bg-white text-xs text-right"
                            />
                          </td>
                          <td className="p-1">
                            <input
                              type="number"
                              step="0.01"
                              value={it.price_per_mt ?? 0}
                              onChange={(e) => setItem(i, { price_per_mt: e.target.value })}
                              data-testid={`item-price-${i}`}
                              className="w-24 h-9 px-2 border border-[#E5E0D8] bg-white text-xs text-right"
                            />
                          </td>
                          <TdSm className="text-right font-semibold">{fmt(it.amount)}</TdSm>
                          <td className="p-1 text-right">
                            <button
                              onClick={() => removeItem(i)}
                              className="text-red-700 hover:bg-red-50 p-1"
                              type="button"
                            >
                              <X size={14} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot className="bg-[#F3EFE7]">
                      <tr>
                        <td colSpan={4} className="p-2 text-right font-bold">TOTAL</td>
                        <TdSm className="text-right font-bold">{fmt(totals.qty, 3)}</TdSm>
                        <td />
                        <TdSm className="text-right font-bold text-[#1A201C]">{fmt(totals.amount)}</TdSm>
                        <td />
                      </tr>
                    </tfoot>
                  </table>
                </div>
                <button
                  type="button"
                  onClick={addItem}
                  className="mt-3 inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-[#DE9E36] hover:text-[#1A201C]"
                >
                  <Plus size={12} /> Add item
                </button>
              </Section>

              {/* Payment schedule */}
              <Section title="Payment schedule">
                <Grid>
                  <Field label="Advance %">
                    <Input
                      type="number"
                      value={editing.advance_pct}
                      onChange={(v) => {
                        const n = Number(v) || 0;
                        setEditing({ ...editing, advance_pct: n, balance_pct: 100 - n });
                      }}
                    />
                  </Field>
                  <Field label="Balance %">
                    <Input
                      type="number"
                      value={editing.balance_pct}
                      onChange={(v) => {
                        const n = Number(v) || 0;
                        setEditing({ ...editing, balance_pct: n, advance_pct: 100 - n });
                      }}
                    />
                  </Field>
                </Grid>
                <div className="mt-3 grid grid-cols-2 gap-4 bg-[#F3EFE7] p-4 text-sm">
                  <div>
                    <div className="overline text-[#4E5750]">Advance due</div>
                    <div className="font-bold text-lg">
                      {editing.currency} {fmt(advanceAmount)}
                    </div>
                  </div>
                  <div>
                    <div className="overline text-[#4E5750]">Balance</div>
                    <div className="font-bold text-lg">
                      {editing.currency} {fmt(balanceAmount)}
                    </div>
                  </div>
                </div>
              </Section>

              {/* Bank */}
              <Section title="Bank details">
                <Grid>
                  <Field label="Beneficiary" wide>
                    <Input value={editing.bank.beneficiary || ""} onChange={(v) => setEditing({ ...editing, bank: { ...editing.bank, beneficiary: v } })} />
                  </Field>
                  <Field label="Bank name & address" wide>
                    <Input value={editing.bank.bank_name || ""} onChange={(v) => setEditing({ ...editing, bank: { ...editing.bank, bank_name: v } })} />
                  </Field>
                  <Field label="IBAN">
                    <Input value={editing.bank.iban || ""} onChange={(v) => setEditing({ ...editing, bank: { ...editing.bank, iban: v } })} />
                  </Field>
                  <Field label="SWIFT / BIC">
                    <Input value={editing.bank.swift || ""} onChange={(v) => setEditing({ ...editing, bank: { ...editing.bank, swift: v } })} />
                  </Field>
                  <Field label="Currency of account">
                    <select
                      value={editing.bank.currency || "EUR"}
                      onChange={(e) => setEditing({ ...editing, bank: { ...editing.bank, currency: e.target.value } })}
                      data-testid="bank-currency"
                      className="w-full h-11 px-3 border border-[#E5E0D8] bg-white text-sm"
                    >
                      <option value="EUR">EUR</option>
                      <option value="USD">USD</option>
                    </select>
                  </Field>
                  <Field label="Payment reference" wide>
                    <Input value={editing.bank.reference || ""} onChange={(v) => setEditing({ ...editing, bank: { ...editing.bank, reference: v } })} />
                  </Field>
                </Grid>
              </Section>

              {/* Shipping terms */}
              <Section title="Shipping terms">
                <Grid>
                  <Field label="Freight component">
                    <Input
                      value={editing.freight_component || ""}
                      onChange={(v) => setEditing({ ...editing, freight_component: v })}
                      placeholder="e.g. EUR 1,250 included in CIF"
                      testId="freight-component"
                    />
                  </Field>
                  <Field label="Estimated transit time">
                    <Input
                      value={editing.estimated_transit_time || ""}
                      onChange={(v) => setEditing({ ...editing, estimated_transit_time: v })}
                      placeholder="e.g. 5-7 days"
                      testId="transit-time"
                    />
                  </Field>
                  <Field label="Container loading">
                    <Input
                      value={editing.container_loading || ""}
                      onChange={(v) => setEditing({ ...editing, container_loading: v })}
                      placeholder="e.g. 20 MT/day"
                      testId="container-loading-rate"
                    />
                  </Field>
                  <Field label="Partial shipment">
                    <select
                      value={editing.partial_shipment || ""}
                      onChange={(e) => setEditing({ ...editing, partial_shipment: e.target.value })}
                      data-testid="partial-shipment"
                      className="w-full h-11 px-3 border border-[#E5E0D8] bg-white text-sm"
                    >
                      <option value="">— Select —</option>
                      <option value="Allowed">Allowed</option>
                      <option value="Not allowed">Not allowed</option>
                    </select>
                  </Field>
                  <Field label="Transshipment">
                    <select
                      value={editing.transshipment || ""}
                      onChange={(e) => setEditing({ ...editing, transshipment: e.target.value })}
                      data-testid="transshipment"
                      className="w-full h-11 px-3 border border-[#E5E0D8] bg-white text-sm"
                    >
                      <option value="">— Select —</option>
                      <option value="Allowed">Allowed</option>
                      <option value="Not allowed">Not allowed</option>
                    </select>
                  </Field>
                </Grid>
              </Section>

              {/* Required shipping documents */}
              <Section title="Required shipping documents">
                <div className="space-y-2">
                  {(editing.shipping_documents || []).map((d, i) => (
                    <div key={i} className="flex gap-2">
                      <span className="w-8 h-11 flex items-center justify-center bg-[#F3EFE7] text-xs font-mono text-[#4E5750]">
                        {i + 1}
                      </span>
                      <input
                        value={d}
                        onChange={(e) => {
                          const copy = [...editing.shipping_documents];
                          copy[i] = e.target.value;
                          setEditing({ ...editing, shipping_documents: copy });
                        }}
                        data-testid={`shipping-doc-${i}`}
                        className="flex-1 h-11 px-3 border border-[#E5E0D8] bg-white text-sm"
                      />
                      <button
                        type="button"
                        onClick={() =>
                          setEditing({
                            ...editing,
                            shipping_documents: editing.shipping_documents.filter((_, idx) => idx !== i),
                          })
                        }
                        className="px-3 border border-[#E5E0D8] hover:bg-red-50 text-red-700"
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() =>
                      setEditing({
                        ...editing,
                        shipping_documents: [...(editing.shipping_documents || []), ""],
                      })
                    }
                    className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-[#DE9E36] hover:text-[#1A201C]"
                  >
                    <Plus size={12} /> Add document
                  </button>
                </div>
              </Section>

              {/* Remarks */}
              <Section title="Remarks">
                <textarea
                  value={editing.remarks || ""}
                  onChange={(e) => setEditing({ ...editing, remarks: e.target.value })}
                  data-testid="invoice-remarks"
                  rows={3}
                  className="w-full p-3 border border-[#E5E0D8] bg-white text-sm"
                />
              </Section>

              {/* Order tracking */}
              <Section title="Order tracking">
                <div className="space-y-2">
                  {TRACKING_STEPS.map((s, i) => {
                    const done = Boolean(editing.tracking?.[s.key]);
                    return (
                      <label
                        key={s.key}
                        data-testid={`tracking-${s.key}`}
                        className={`flex items-center gap-3 p-3 border cursor-pointer transition-colors ${
                          done ? "border-[#DE9E36] bg-[#F3EFE7]" : "border-[#E5E0D8] bg-white hover:bg-[#FDFBF7]"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={done}
                          onChange={() =>
                            setEditing({
                              ...editing,
                              tracking: { ...(editing.tracking || {}), [s.key]: !done },
                            })
                          }
                          className="w-4 h-4 accent-[#DE9E36]"
                        />
                        <span className="text-xs font-mono text-[#4E5750] w-14">Step {i + 1}</span>
                        <span className={`text-sm ${done ? "text-[#1A201C] font-semibold" : "text-[#4E5750]"}`}>
                          {s.label}
                        </span>
                        {done && <span className="ml-auto text-[#DE9E36] text-xs tracking-[0.2em] uppercase">Done</span>}
                      </label>
                    );
                  })}
                </div>
                <div className="mt-4">
                  <div className="overline text-[#4E5750] mb-2">Client remarks</div>
                  <textarea
                    value={editing.client_remarks || ""}
                    onChange={(e) => setEditing({ ...editing, client_remarks: e.target.value })}
                    data-testid="client-remarks"
                    rows={3}
                    placeholder="Internal notes about the client (not printed on the PDF)…"
                    className="w-full p-3 border border-[#E5E0D8] bg-white text-sm"
                  />
                </div>
              </Section>

              {/* Signature */}
              <Section title="Signature & stamp">
                <Grid>
                  <Field label="Signatory name">
                    <Input value={editing.signature_name || ""} onChange={(v) => setEditing({ ...editing, signature_name: v })} />
                  </Field>
                  <Field label="Signatory title">
                    <Input value={editing.signature_title || ""} onChange={(v) => setEditing({ ...editing, signature_title: v })} />
                  </Field>
                  <Field label="Validity (days)">
                    <Input
                      type="number"
                      value={editing.validity_days ?? 10}
                      onChange={(v) => setEditing({ ...editing, validity_days: Number(v) || 10 })}
                      testId="validity-days"
                      placeholder="10"
                    />
                  </Field>
                  <Field label="Logo on PDF">
                    <select
                      value={editing.logo_position || "left"}
                      onChange={(e) => setEditing({ ...editing, logo_position: e.target.value })}
                      data-testid="logo-position"
                      className="w-full h-11 px-3 border border-[#E5E0D8] bg-white text-sm"
                    >
                      <option value="left">Top left</option>
                      <option value="right">Top right</option>
                      <option value="none">Hidden</option>
                    </select>
                  </Field>
                  <Field label="Company stamp (PNG with transparent bg recommended)" wide>
                    <div className="flex items-start gap-4">
                      <div className="w-32 h-32 bg-[#F3EFE7] border border-[#E5E0D8] flex items-center justify-center overflow-hidden">
                        <img
                          src={editing.stamp_image || "/stamp-default.png"}
                          alt="Stamp"
                          className="max-w-full max-h-full object-contain"
                        />
                      </div>
                      <div className="flex-1 space-y-2">
                        <div className="text-xs text-[#4E5750]">
                          {editing.stamp_image
                            ? "Custom stamp uploaded for this invoice."
                            : "Using the default EKOBIOTEK stamp. Upload another to override."}
                        </div>
                        <label className="inline-flex items-center gap-2 px-4 py-3 bg-[#1A201C] text-[#DE9E36] text-xs tracking-[0.2em] uppercase cursor-pointer hover:bg-[#DE9E36] hover:text-[#1A201C] transition-colors">
                          Upload stamp
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            data-testid="stamp-upload"
                            onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (!file) return;
                              try {
                                const fd = new FormData();
                                fd.append("file", file);
                                const { data } = await api.post("/admin/upload", fd, {
                                  headers: { "Content-Type": "multipart/form-data" },
                                });
                                setEditing((p) => ({ ...p, stamp_image: data.url }));
                                toast.success("Stamp uploaded");
                              } catch (err) {
                                toast.error(formatApiError(err));
                              }
                            }}
                          />
                        </label>
                        {editing.stamp_image && (
                          <button
                            type="button"
                            onClick={() => setEditing({ ...editing, stamp_image: "" })}
                            className="block text-xs text-red-700 hover:underline"
                          >
                            Reset to default stamp
                          </button>
                        )}
                      </div>
                    </div>
                  </Field>
                </Grid>
              </Section>
            </div>

            <div className="sticky bottom-0 bg-[#FDFBF7] border-t border-[#E5E0D8] px-6 py-4 flex justify-end gap-3">
              <button
                onClick={() => setEditing(null)}
                className="px-5 py-3 text-xs tracking-[0.22em] uppercase border border-[#E5E0D8]"
              >
                Cancel
              </button>
              <button
                onClick={save}
                data-testid="save-invoice"
                className="inline-flex items-center gap-2 px-5 py-3 bg-[#DE9E36] text-[#1A201C] text-xs tracking-[0.22em] uppercase font-bold hover:bg-[#1A201C] hover:text-[#DE9E36] transition-colors"
              >
                <Save size={14} /> Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Primitives
const Section = ({ title, children }) => (
  <div>
    <div className="overline text-[#DE9E36] mb-3">{title}</div>
    {children}
  </div>
);
const Grid = ({ children }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">{children}</div>
);
const Field = ({ label, wide, children }) => (
  <div className={wide ? "sm:col-span-2 lg:col-span-3" : ""}>
    <div className="overline text-[#4E5750] mb-1.5">{label}</div>
    {children}
  </div>
);
const Input = ({ value, onChange, type = "text", placeholder, testId }) => (
  <input
    type={type}
    value={value ?? ""}
    placeholder={placeholder}
    data-testid={testId}
    onChange={(e) => onChange(e.target.value)}
    className="w-full h-11 px-3 border border-[#E5E0D8] bg-white text-sm focus:outline-none focus:border-[#DE9E36]"
  />
);
const Th = ({ children, className = "" }) => (
  <th className={`px-5 py-3 overline text-[#4E5750] ${className}`}>{children}</th>
);
const Td = ({ children, className = "" }) => (
  <td className={`px-5 py-4 align-middle ${className}`}>{children}</td>
);
const ThSm = ({ children, className = "" }) => (
  <th className={`px-2 py-2 text-[10px] tracking-[0.2em] uppercase text-[#4E5750] text-left ${className}`}>
    {children}
  </th>
);
const TdSm = ({ children, className = "" }) => (
  <td className={`px-2 py-2 align-middle ${className}`}>{children}</td>
);

export default AdminInvoices;
